import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { FileTextIcon } from '@navikt/aksel-icons';
import { useSelectedUnitContext } from '../../contexts/selectedUnitContext';
import { useTeamSurveyOverviewQuery } from '../../hooks/queries/useTeamSurveyOverviewQuery';
import { useDeleteWorkEnvironmentSurveyMutation } from '../../hooks/mutations/useDeleteWorkEnvironmentSurveyMutation';
import { Routes } from '../../enums/Routes';
import { EmptyState } from '../../components/EmptyState';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { overviewEntries, primarySurveyId, surveyWithAreasLeft } from './surveyOverviewView';
import { AreasLeftCard } from './components/AreasLeftCard';
import { SurveyCard } from './components/SurveyCard';
import { MeasurementNote } from './components/MeasurementNote';
import classes from './WorkEnvironmentDashboardPage.module.css';

const K = 'workEnvironmentDashboard';

/** The team leader's surveys of the psychosocial work environment, newest first */
export const WorkEnvironmentDashboardPage = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { selectedTeamId: teamId } = useSelectedUnitContext();
  // The names of the areas left are in the language of the questions
  const language = questionLanguageFor(i18n.language);
  const { data: surveys, isLoading, isError } = useTeamSurveyOverviewQuery(teamId, language);
  const deleteSurvey = useDeleteWorkEnvironmentSurveyMutation(teamId);

  if (isLoading) {
    return (
      <div className={classes.container}>
        <Paragraph className={classes.subtle}>{t(`${K}.loading`)}</Paragraph>
      </div>
    );
  }

  if (isError || !surveys) {
    return (
      <div className={classes.container}>
        <Alert data-color="danger">{t(`${K}.error`)}</Alert>
      </div>
    );
  }

  if (surveys.length === 0) {
    return (
      <div className={classes.container}>
        <EmptyState
          icon={<FileTextIcon aria-hidden fontSize="48px" />}
          title={t(`${K}.emptyState.title`)}
          description={t(`${K}.emptyState.description`)}
          timeline={t(`${K}.emptyState.timeline`)}
          actionLabel={t(`${K}.emptyState.action`)}
          onAction={() => navigate(Routes.CreateSurvey)}
        />
      </div>
    );
  }

  const primaryId = primarySurveyId(surveys);
  const withAreasLeft = surveyWithAreasLeft(surveys);
  const remove = async (surveyId: number) => {
    await deleteSurvey.mutateAsync(surveyId);
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.heading}>
          <Heading level={2} data-size="sm">
            {t(`${K}.title`)}
          </Heading>
          <Paragraph data-size="sm" className={classes.subtle}>
            {t(`${K}.order`)}
          </Paragraph>
        </div>
        <Button asChild variant="secondary" data-size="sm">
          <RouterLink to={Routes.CreateSurvey}>{t(`${K}.newSurvey`)}</RouterLink>
        </Button>
      </div>
      {withAreasLeft && <AreasLeftCard survey={withAreasLeft} />}
      <ol aria-label={t(`${K}.listLabel`)} className={classes.list}>
        {overviewEntries(surveys).map((entry) =>
          entry.kind === 'survey' ? (
            <li key={`survey-${entry.survey.id}`}>
              <SurveyCard survey={entry.survey} primaryId={primaryId} onDelete={remove} />
            </li>
          ) : (
            <li key={`measurement-${entry.to}`}>
              <MeasurementNote entry={entry} />
            </li>
          ),
        )}
      </ol>
    </div>
  );
};
